/**
 * PatrimoineMapPage (SANDBOX) — gestion de patrimoine cartographique.
 * Carte sombre ultra-minimaliste SANS AUCUN POI (tuiles dark_nolabels =
 * géographie pure) ; l'API de tuiles ne reçoit que des requêtes {z}/{x}/{y}.
 * Les données patrimoniales vivent dans le Device Master (AES-GCM local) et
 * sont dessinées par le moteur de calques Canvas 100 % local.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { ShieldCheck, Lock } from 'lucide-react';
import { Dock, LAYERS } from './Dock';
import { LayerCanvas } from './LayerCanvas';
import { AssetWidget } from './AssetWidget';
import { MajordomePanel } from './MajordomePanel';
import { loadOrSeedVault, anonymizedAggregates, eur } from './deviceVault';

// Géographie pure : aucun label, aucun POI natif (contrat « API aveugle »).
// Esri Dark Gray Canvas (base sans labels), keyless.
const DARK_TILES = 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}';
const ATTRIB = '&copy; Esri &copy; OpenStreetMap contributors';

export default function PatrimoineMapPage() {
  const [assets, setAssets] = useState([]);
  const [activeLayer, setActiveLayer] = useState('immobilier');
  const [selected, setSelected] = useState(null);
  const [majordomeOpen, setMajordomeOpen] = useState(false);
  const canvasApi = useRef(null);

  useEffect(() => { loadOrSeedVault().then(setAssets); }, []);

  const layerMeta = LAYERS.find((l) => l.key === activeLayer);
  const layerAssets = useMemo(() => assets.filter((a) => a.layer === activeLayer), [assets, activeLayer]);
  const aggregates = useMemo(() => anonymizedAggregates(layerAssets), [layerAssets]);
  const layerTotal = layerAssets.reduce((s, a) => s + a.value, 0);

  return (
    <div className="relative w-full h-screen overflow-hidden" style={{ background: '#07080B' }}
         data-testid="patrimoine-page">
      <MapContainer center={[47.2, 2.4]} zoom={6} maxZoom={16} zoomControl={false} attributionControl={false}
                    className="absolute inset-0" style={{ background: '#07080B' }}>
        <TileLayer url={DARK_TILES} attribution={ATTRIB} />
        <LayerCanvas ref={canvasApi} assets={layerAssets} color={layerMeta.color}
                     onAssetClick={(a) => setSelected(a)} />
      </MapContainer>

      {/* En-tête minimaliste */}
      <div className="absolute top-4 left-4 z-[700]" data-testid="patrimoine-header">
        <h1 className="text-[15px] font-black tracking-tight text-white">
          PSU <span style={{ color: layerMeta.color }}>· Patrimoine</span>
        </h1>
        <p className="text-[10px] text-gray-500 flex items-center gap-1 mt-0.5">
          <Lock className="w-2.5 h-2.5 text-emerald-400" /> Device Master — données chiffrées sur cet appareil
        </p>
      </div>

      {/* Synthèse du calque actif (glass) */}
      <div className="absolute top-4 right-4 z-[700] px-3.5 py-2.5 rounded-xl"
           style={{ background: 'rgba(16,17,22,0.55)', backdropFilter: 'blur(16px)', border: `1px solid ${layerMeta.color}45` }}
           data-testid="patrimoine-layer-summary">
        <p className="text-[9.5px] uppercase tracking-[0.15em] text-gray-500">{layerMeta.label}</p>
        <p className="text-[18px] font-black tabular-nums" style={{ color: layerMeta.color }}
           data-testid="patrimoine-layer-total">{eur(layerTotal)}</p>
        <p className="text-[9.5px] text-gray-500">{layerAssets.length} position(s)</p>
      </div>

      {/* Bandeau contrat de confidentialité */}
      <div className="absolute bottom-5 left-4 z-[700] flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[9.5px] text-gray-400"
           style={{ background: 'rgba(16,17,22,0.5)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <ShieldCheck className="w-3 h-3 text-emerald-400" />
        L'API carte ne reçoit que des tuiles — zéro métadonnée financière
      </div>

      {selected && (
        <AssetWidget asset={selected} color={layerMeta.color} onClose={() => setSelected(null)} />
      )}

      <Dock active={activeLayer}
            onSelect={(k) => { setActiveLayer(k); setSelected(null); }}
            majordomeOpen={majordomeOpen}
            onToggleMajordome={() => setMajordomeOpen((v) => !v)} />

      <MajordomePanel open={majordomeOpen} onClose={() => setMajordomeOpen(false)}
                      layer={activeLayer} layerLabel={layerMeta.label} color={layerMeta.color}
                      aggregates={aggregates}
                      getSnapshot={() => canvasApi.current?.snapshot()} />
    </div>
  );
}
